import { memo, ReactNode, useContext, type JSX } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ButtonVariant, Icon, Informer, InformerProps, LinkAsButton, Typography, VerticalTabs } from '@/shared/ui';

import { Breakpoints, useMediaQuery } from '@/hooks/useMediaQuery';

import { OTHER_ROUTES, ROUTES, ROUTES_BY_SERVICE_CODE } from '@/router/config';

import AuthContext from '@/contexts/Auth/AuthContext';

import './CabinetTemplateStyles.scss';

const CABINET_NAVIGATION: Props['navigationData'] = [
  {
    label: 'Профіль користувача',
    slug: ROUTES.USER.PROFILE,
  },
  {
    label: 'Дозволи',
    slug: ROUTES_BY_SERVICE_CODE['erz.extract'],
  },
  {
    label: 'Довідки та витяги',
    slug: ROUTES_BY_SERVICE_CODE['CEAA03'],
  },
  {
    label: 'Страхування',
    slug: OTHER_ROUTES.bia.INSURANCE_ORDERS,
  },
  {
    label: 'Квитанції про оплату',
    slug: OTHER_ROUTES.pay.PAYMENT_RECEIPTS,
  },
];

interface Props {
  children?: ReactNode;
  informer?: InformerProps;
  navigationData?: Array<{ label: string; slug: string }>;
  title?: string;
}

export const CabinetTemplate = memo(({ children, title, informer }: Props): JSX.Element => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isLgSize = useMediaQuery(Breakpoints.LG);
  const isMdSize = useMediaQuery(Breakpoints.MD);
  const { logout } = useContext(AuthContext);

  const tabs = CABINET_NAVIGATION.map((tab) => ({
    key: tab.slug,
    label: tab.label,
    icon: <Icon name="chevron_right" />,
  }));

  return (
    <div className="cabinet-template">
      <div className="container">
        <div className="cabinet-template__wrapper-inner">
          <Typography normal className="cabinet-template__title" as="h1" variant="H1">
            Особистий кабінет
          </Typography>
          <aside className="cabinet-template__navigation">
            <VerticalTabs defaultActiveKey={pathname} items={tabs} onChange={navigate} />

            {informer && !isMdSize && <Informer {...informer} />}
            {!isLgSize && (
              <LinkAsButton
                to={ROUTES.LOGIN}
                state={{
                  nextPage: pathname,
                }}
                variant={ButtonVariant.WARNING}
                onClick={(): void => logout()}
              >
                Вийти з системи
              </LinkAsButton>
            )}
          </aside>

          <div className="cabinet-template__content-wrapper">
            {title && !isMdSize && (
              <Typography variant="H2" className="cabinet-template__tab-title">
                {title}
              </Typography>
            )}
            <section className="cabinet-template__content">{children}</section>
          </div>

          {informer && isMdSize && <Informer {...informer} />}
        </div>
      </div>
    </div>
  );
});
